// CONTRACT:C2-RENDER-ENGINE.1.0
/** RENDER TUNING — operator-tweakable render knobs loaded from an EXTERNAL config file
 *  (`render-tuning.json`, served beside the app) so look-and-feel / density numbers can be
 *  adjusted WITHOUT a rebuild: edit the JSON, reload the page. The render path reads the
 *  live {@link tuning} object at use-time, so values fetched after first paint apply on the
 *  next rebuild (~1 s feed cadence).
 *
 *  FAIL-TO-DEFAULT BY CONSTRUCTION (this is a presentation surface, but a hostile/garbled
 *  config must never wedge the render loop):
 *    - fetch/parse/shape errors  → compiled defaults, one console.warn (never throws);
 *    - unknown keys              → ignored (warned) — a typo cannot silently do nothing AND
 *                                  a future key in an old build cannot break it;
 *    - non-finite values         → rejected per-key;
 *    - out-of-range values       → CLAMPED into the per-key bounds below. The bounds are
 *                                  load-bearing: labelDensityKeep feeds a near-O(N²) exact
 *                                  deconfliction pass, so an unbounded value is a freeze.
 *  PRESENTATION ONLY (I13): nothing here touches data, PDP, or clearance — only how much
 *  decoration/labeling the deck draws. */

export interface RenderTuning {
  /** Base marker radius, screen px (zoom-independent). [was MARKER_PX] */
  markerPx: number;
  /** Soft glow radius behind each marker, px. [was HALO_PX] */
  haloPx: number;
  /** Base callsign font size, px, before declutter shrink. [was CALLSIGN_BASE_SIZE_PX] */
  callsignBaseSizePx: number;
  /** Labels the shrink fallback would drive below this size are HIDDEN, not drawn tiny. [was MIN_LABEL_PX] */
  minLabelPx: number;
  /** Above this many on-screen labels, lowest-priority tiers are density-culled. Bounded — feeds a near-O(N²) pass. [was LABEL_DENSITY_KEEP] */
  labelDensityKeep: number;
  /** Sparse-view "show all callsigns": viewport holds at most this many tracks ⇒ label gates relax. 0 disables. [was SPARSE_LABEL_SHOW_ALL] */
  sparseLabelShowAll: number;
  /** Longest properties.name accepted as a label via the fallback in a NORMAL (dense) view. */
  labelNameMax: number;
  /** Longest properties.name accepted as a label in a SPARSE view (room exists — alert headlines fit). */
  sparseLabelNameMax: number;
  /** CLUSTER-LATCH grouping radius (px): label anchors within this screen distance of each other
   *  (single-linkage) group into one cluster for the latch pass. */
  labelClusterRadiusPx: number;
  /** CLUSTER-LATCH threshold: a proximity group with at least this many surviving labels LATCHES
   *  into ONE left-justified stacked column beside the group (full base size — the latch IS the
   *  declutter). Values below 2 disable the pass (0 is the documented "off"; a threshold of 1
   *  would latch every solo label, which is meaningless). */
  labelClusterLatch: number;
  /** Row CAP per latched column: a longer column renders this many rows + one "…" cue row
   *  (zoom in to expand); overflow labels hide. <=0 = unlimited. */
  labelClusterMaxRows: number;
  /** PROTECTIVE TEXT PLATE alpha [0..0.9] behind every callsign label (theme-coloured backing —
   *  the primary legibility device over a saturated icon group). 0 disables the plate entirely,
   *  restoring the full 2px SDF outline so legibility never regresses when off. */
  labelPlateAlpha: number;
  /** Positions kept per track for the comet-tail motion trail. [was TRAIL_LEN] */
  trailLen: number;
  /** TRAIL TIME-GAP BREAK (s): a track untouched by the render loop for longer than this (hidden/
   *  throttled tab — rAF paused while the feed kept moving it) RESTARTS its trail instead of
   *  drawing one long segment across the jump (the errant-flight-path fix; trailPolicy.ts). */
  trailMaxGapS: number;
  /** TRAIL MAX AGE (s): stored trail points older than this are pruned, AND the renderer fades
   *  each segment toward zero alpha/width as it approaches this age — the contrail dissipation. */
  trailMaxAgeS: number;
  /** TRAIL LONG-SEGMENT DEMOTION (nm): a trail segment longer than this renders at the faint
   *  TAIL styling, never the bright head — defense-in-depth so a throttled-rAF window's outsized
   *  step can never read as a crisp flight path (segments past the 60nm teleport cap still drop
   *  entirely). */
  trailLongSegNm: number;
  /** TRAIL HEAD WIDTH (px): the widest (just-now) trail segment; the ramp thins from here to the
   *  fixed 0.8px tail. Slimmer default than the old 2.5 for the contrail feel. [was TRAIL_HEAD_PX] */
  trailHeadPx: number;
  /** Above this many entities the per-rebuild decoration overlays drop (dense mode). [was DECORATION_MAX_ENTITIES] */
  decorationMaxEntities: number;
  /** Above this many entities the per-marker halo-overlap detune is skipped (fails to full opacity). [was HALO_DETUNE_MAX_ENTITIES] */
  haloDetuneMaxEntities: number;
  /** ICON-DENSITY LOD ramp START: at/below this many in-viewport icons the LOD is fully off. */
  iconLodLo: number;
  /** ICON-DENSITY LOD ramp END: at/above this many in-viewport icons the LOD is fully on.
   *  Must exceed iconLodLo — enforced at apply-time (Lo is clamped to Hi−1). */
  iconLodHi: number;
  /** Max SIZE reduction at full density for the lowest-priority tier (neutral, globalRank 5)
   *  [0..0.8] — the bound keeps the rank-5 floor ≥0.2 of full size (densityLodMul, never
   *  zero/negative). Blue force never shrinks while blueForceAlwaysProminent is ON — an
   *  EXPLICIT exemption branch in deckBackend densitySizeMul (the old "rank 0 is
   *  mathematically immune" story is gone: globalRank has no rank-0 icon tier). */
  iconLodShrink: number;
  /** Max OPACITY reduction at full density for the lowest-priority tier (neutral, globalRank 5)
   *  [0..0.9] — bound ⇒ alpha floor ≥0.1. Friend exemption as iconLodShrink. */
  iconLodFade: number;
}

/** Compiled defaults — the exact values previously hardcoded in deckBackend.ts. */
export const RENDER_TUNING_DEFAULTS: Readonly<RenderTuning> = Object.freeze({
  markerPx: 5.5,
  haloPx: 13,
  callsignBaseSizePx: 11,
  minLabelPx: 9,
  labelDensityKeep: 120,
  sparseLabelShowAll: 24,
  labelNameMax: 18,
  sparseLabelNameMax: 40,
  labelClusterRadiusPx: 26,
  labelClusterLatch: 5,
  labelClusterMaxRows: 20,
  labelPlateAlpha: 0.55,
  trailLen: 48,
  trailMaxGapS: 30,
  trailMaxAgeS: 300,
  trailLongSegNm: 8,
  trailHeadPx: 2.0,
  decorationMaxEntities: 12_000,
  haloDetuneMaxEntities: 10_000,
  iconLodLo: 150,
  iconLodHi: 1200,
  iconLodShrink: 0.45,
  iconLodFade: 0.55,
});

/** Per-key [min, max] clamp bounds. Chosen so the most hostile in-range value still renders:
 *  labelDensityKeep ≤ 400 keeps the exact deconfliction pass bounded; the entity caps stay
 *  within what the decoration builders can construct per rebuild without starving the frame. */
const BOUNDS: Readonly<Record<keyof RenderTuning, readonly [number, number]>> = Object.freeze({
  markerPx: [2, 16],
  haloPx: [0, 40],
  callsignBaseSizePx: [7, 20],
  minLabelPx: [5, 16],
  labelDensityKeep: [0, 400],
  sparseLabelShowAll: [0, 200],
  labelNameMax: [4, 64],
  sparseLabelNameMax: [4, 80],
  // Latch radius stays within a sane screen neighbourhood; the threshold's lower bound is 0 so
  // the documented "0 disables" survives the clamp (use-site treats <2 as off — see the interface
  // doc); plate alpha ≤0.9 so a plate can never fully paint out the basemap under a label field.
  labelClusterRadiusPx: [8, 80],
  labelClusterLatch: [0, 20],
  labelClusterMaxRows: [0, 200],
  labelPlateAlpha: [0, 0.9],
  trailLen: [2, 240],
  trailMaxGapS: [5, 600],
  trailMaxAgeS: [10, 3600],
  trailLongSegNm: [1, 60],
  trailHeadPx: [0.5, 6],
  decorationMaxEntities: [0, 200_000],
  haloDetuneMaxEntities: [0, 100_000],
  iconLodLo: [0, 5000],
  iconLodHi: [50, 20_000],
  iconLodShrink: [0, 0.8],
  iconLodFade: [0, 0.9],
});

const live: RenderTuning = { ...RENDER_TUNING_DEFAULTS };

/** The LIVE tuning values. Render code reads through this at USE time (never captures a copy),
 *  so a config that arrives after first paint applies on the next rebuild. */
export const tuning: Readonly<RenderTuning> = live;

/** Apply a parsed config object onto the live tuning (validate → clamp → assign).
 *  Total: never throws; returns what happened per key for the boot log/tests. */
export function applyRenderTuning(raw: unknown): { applied: string[]; ignored: string[] } {
  const applied: string[] = [];
  const ignored: string[] = [];
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return { applied, ignored: ["<not an object>"] };
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (key.startsWith("_")) continue; // "_readme"-style comment keys — JSON has no comments
    if (!(key in RENDER_TUNING_DEFAULTS)) {
      ignored.push(`${key} (unknown key)`);
      continue;
    }
    const k = key as keyof RenderTuning;
    const n = typeof value === "number" ? value : NaN;
    if (!Number.isFinite(n)) {
      ignored.push(`${key} (not a finite number)`);
      continue;
    }
    const [lo, hi] = BOUNDS[k];
    live[k] = Math.min(hi, Math.max(lo, n));
    applied.push(key);
  }
  // CROSS-KEY CONSISTENCY (total, never throws): the icon-density LOD ramp needs Hi > Lo, so a
  // config that inverts them clamps Lo down to Hi−1 (the bounds guarantee Hi ≥ 50, so the result
  // stays inside iconLodLo's own [0,5000] range). Keeps the densityFactor denominator positive.
  if (live.iconLodLo >= live.iconLodHi) {
    live.iconLodLo = live.iconLodHi - 1;
    ignored.push("iconLodLo (clamped to iconLodHi-1 — Lo must be < Hi)");
  }
  return { applied, ignored };
}

/** Fetch `render-tuning.json` from beside the app (BASE_URL-aware; guarded for headless tsx
 *  where import.meta.env is absent) and apply it. Any failure ⇒ compiled defaults stand. */
export async function loadRenderTuning(): Promise<void> {
  let base = "/";
  try {
    base = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? "/";
  } catch {
    /* non-Vite context — root-relative is the repo convention (kas.ts) */
  }
  const url = `${base.endsWith("/") ? base : `${base}/`}render-tuning.json`;
  try {
    const res = await fetch(url);
    if (!res.ok) return; // absent config is a fully supported state — defaults stand, silently
    const { applied, ignored } = applyRenderTuning(await res.json());
    if (ignored.length > 0) console.warn(`[render-tuning] ignored: ${ignored.join(", ")}`);
    if (applied.length > 0) console.info(`[render-tuning] applied: ${applied.join(", ")}`);
  } catch (err) {
    console.warn("[render-tuning] load failed — compiled defaults in effect", err);
  }
}
